import { Section, Session } from "@models/ISchema";
import { EditedSession } from "../context/formData.reducer";

/**
 * `sectionAdapter` runs every session of the link through `sessionAdapter`, so
 * the sessions hanging from `link.sections` are `Session` and `EditedSession` at
 * the same time: they keep the raw fields (`assignment`, `section`, ...) and
 * gain the edited ones (`blocks`, `intervals`, ...).
 */
export type OriginalSession = Session & EditedSession;

/**
 * Index the link's sessions by id.
 *
 * What this returns is the untouched assignment of each session, which is the
 * only reliable baseline once the user started editing: a saved session's
 * `session` field stops pointing at the original as soon as the session is
 * re-selected from a saved card, since it then holds the previously edited
 * session instead.
 *
 * Time complexity: O(n)
 * Where `n` is the total number of sessions across the given sections.
 *
 * @param sections Section[]
 * @return { [sessionId: string]: OriginalSession }
 */
export const getOriginalSessionsById = (
  sections: Section[],
): { [sessionId: string]: OriginalSession } => {
  const result: { [sessionId: string]: OriginalSession } = {};

  (sections ?? []).forEach(section => {
    const allSessions = [...(section.sessions ?? []), ...(section.unasignedSessions ?? [])];
    allSessions.forEach(session => {
      result[session?.id] = session as OriginalSession;
    });
  });

  return result;
};
